import type { IMediaRepository } from "@/core/repositories/interfaces/IMediaRepository";
import {
  ARTICLE_COVER_IMAGE_BUCKET,
  EVENT_COVER_IMAGE_BUCKET,
  PLACE_COVER_IMAGE_BUCKET,
  TAXONOMY_ICON_BUCKET,
  getCoverImageExtension,
  isAcceptedCoverImageType,
  isAcceptedTaxonomyIconType,
} from "@/core/services/types/media.types";
import { createClient } from "@/lib/supabase";

export class MediaRepository implements IMediaRepository {
  async uploadArticleCoverImage(articleId: string, file: File) {
    if (!isAcceptedCoverImageType(file.type)) {
      return {
        success: false as const,
        error: "Unsupported image type",
      };
    }

    const supabase = await createClient();
    const extension = getCoverImageExtension(file.type);
    const path = `${articleId}/${crypto.randomUUID()}.${extension}`;

    const { error } = await supabase.storage
      .from(ARTICLE_COVER_IMAGE_BUCKET)
      .upload(path, file, {
        contentType: file.type,
        upsert: false,
      });

    if (error) {
      console.error("[MediaRepository.uploadArticleCoverImage]", error.message);
      return {
        success: false as const,
        error: error.message,
      };
    }

    const { data } = supabase.storage
      .from(ARTICLE_COVER_IMAGE_BUCKET)
      .getPublicUrl(path);

    return {
      success: true as const,
      publicUrl: data.publicUrl,
    };
  }

  async uploadEventCoverImage(eventId: string, file: File) {
    if (!isAcceptedCoverImageType(file.type)) {
      return {
        success: false as const,
        error: "Unsupported image type",
      };
    }

    const supabase = await createClient();
    const extension = getCoverImageExtension(file.type);
    const path = `${eventId}/${crypto.randomUUID()}.${extension}`;

    const { error } = await supabase.storage
      .from(EVENT_COVER_IMAGE_BUCKET)
      .upload(path, file, {
        contentType: file.type,
        upsert: false,
      });

    if (error) {
      console.error("[MediaRepository.uploadEventCoverImage]", error.message);
      return {
        success: false as const,
        error: error.message,
      };
    }

    const { data } = supabase.storage
      .from(EVENT_COVER_IMAGE_BUCKET)
      .getPublicUrl(path);

    return {
      success: true as const,
      publicUrl: data.publicUrl,
    };
  }

  async uploadPlaceCoverImage(placeId: string, file: File) {
    if (!isAcceptedCoverImageType(file.type)) {
      return {
        success: false as const,
        error: "Unsupported image type",
      };
    }

    const supabase = await createClient();
    const extension = getCoverImageExtension(file.type);
    const path = `${placeId}/${crypto.randomUUID()}.${extension}`;

    const { error } = await supabase.storage
      .from(PLACE_COVER_IMAGE_BUCKET)
      .upload(path, file, {
        contentType: file.type,
        upsert: false,
      });

    if (error) {
      console.error("[MediaRepository.uploadPlaceCoverImage]", error.message);
      return {
        success: false as const,
        error: error.message,
      };
    }

    const { data } = supabase.storage
      .from(PLACE_COVER_IMAGE_BUCKET)
      .getPublicUrl(path);

    return {
      success: true as const,
      publicUrl: data.publicUrl,
    };
  }

  async uploadTaxonomyIcon(file: File) {
    if (!isAcceptedTaxonomyIconType(file.type)) {
      return {
        success: false as const,
        error: "Unsupported icon type",
      };
    }

    const supabase = await createClient();
    const path = `promoted/${crypto.randomUUID()}.svg`;

    const { error } = await supabase.storage
      .from(TAXONOMY_ICON_BUCKET)
      .upload(path, file, {
        contentType: "image/svg+xml",
        upsert: false,
      });

    if (error) {
      console.error("[MediaRepository.uploadTaxonomyIcon]", error.message);
      return {
        success: false as const,
        error: error.message,
      };
    }

    const { data } = supabase.storage
      .from(TAXONOMY_ICON_BUCKET)
      .getPublicUrl(path);

    return {
      success: true as const,
      publicUrl: data.publicUrl,
    };
  }
}
